/**
 * Phase 4b — Modal d'assistant vocal enrichi (dictée + texte libre).
 *
 * UX :
 * - Modal plein écran (mobile-first, safe area iOS)
 * - Deux tabs : "Dicter" (MediaRecorder webm/opus) et "Coller texte" (mode bruyant)
 * - Après réponse serveur : preview des lignes structurées avec checkbox par ligne + édition rapide
 * - Bouton "Injecter dans le devis" applique les lignes sélectionnées au state React
 * - L'autosave hook `useQuoteDraftAutosave` déclenche saveNow() automatiquement via le
 *   changement dirty du quote — pas besoin d'appel serveur explicite
 * - Bouton retour clair (X en haut à droite + label "Fermer")
 *
 * Contrat serveur (DECISIONS.md § IA — Métriques) :
 * - Aucune persistance d'audio ni de transcription — le back ne fait que du preview
 * - Le front insère les lignes retenues dans `quote.lines` via setQuote()
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mic, Square, X, PlusCircle, AlertTriangle, Sparkles, Type } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import BENumericInput from "@/components/quote/BENumericInput";

function formatElapsed(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function LineRowPreview({ line, checked, onToggle, onEdit }) {
  const isCoco = line.vat_regime === "cocontractant";
  const isOff = !line.catalog_item_id;
  return (
    <div
      className="grid grid-cols-[24px_1fr_80px_60px_80px_60px] items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2"
      data-testid={`preview-line-${line._idx}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="h-4 w-4 accent-[#C4553B]"
        data-testid={`preview-line-check-${line._idx}`}
      />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <Input
            value={line.description}
            onChange={(e) => onEdit({ description: e.target.value })}
            className="min-w-0 flex-1 h-7 border-slate-700 bg-slate-950/40 text-xs text-slate-100"
            data-testid={`preview-line-desc-${line._idx}`}
          />
          {isOff && (
            <Badge className="border-[#D99A2B]/40 bg-[#D99A2B]/15 text-[#F0C572] whitespace-nowrap">
              Hors catalogue
            </Badge>
          )}
          {isCoco && (
            <Badge className="border-purple-500/40 bg-purple-500/15 text-purple-300 whitespace-nowrap">
              Cocontractant
            </Badge>
          )}
          {line.needs_review && (
            <Badge className="border-red-500/40 bg-red-500/15 text-red-300 whitespace-nowrap">
              <AlertTriangle className="mr-1 h-3 w-3" /> À vérifier
            </Badge>
          )}
        </div>
        <div className="mt-0.5 text-[10px] text-slate-500">
          Section : {line.section || "Divers"}
          {line.review_reason ? ` — ${line.review_reason}` : ""}
        </div>
      </div>
      <BENumericInput
        value={line.quantity}
        onChange={(n) => onEdit({ quantity: n })}
        className="h-7 border-slate-700 bg-slate-950/40 text-xs text-slate-100 tabular"
      />
      <Input
        value={line.unit}
        onChange={(e) => onEdit({ unit: e.target.value })}
        className="h-7 border-slate-700 bg-slate-950/40 text-xs text-slate-100"
      />
      <BENumericInput
        value={line.unit_price ?? 0}
        onChange={(n) => onEdit({ unit_price: n })}
        className="h-7 border-slate-700 bg-slate-950/40 text-xs text-slate-100 tabular"
      />
      <div className="text-right text-xs text-slate-300 tabular">{line.vat_rate}%</div>
    </div>
  );
}

export default function DictateModal({ quoteId, open, onClose, onInject }) {
  const [tab, setTab] = useState("audio");
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [freeText, setFreeText] = useState("");
  const [result, setResult] = useState(null); // { lines, raw_notes, transcription, ... }
  const [checked, setChecked] = useState({}); // idx → bool

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const reset = () => {
    setIsRecording(false);
    setIsProcessing(false);
    setElapsed(0);
    setFreeText("");
    setResult(null);
    setChecked({});
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    chunksRef.current = [];
    mediaRecorderRef.current = null;
    streamRef.current = null;
  };

  useEffect(() => {
    if (!open) reset();
    return () => reset();
  }, [open]);

  const handleResult = (data) => {
    const withIdx = (data.lines || []).map((l, i) => ({ ...l, _idx: i }));
    setResult({ ...data, lines: withIdx });
    const preChecked = {};
    withIdx.forEach((l) => {
      preChecked[l._idx] = true;
    });
    setChecked(preChecked);
    toast.success(`${withIdx.length} ligne(s) détectée(s)`, {
      description: `${data.catalog_matches_count || 0} match(s) catalogue · ${data.off_catalog_count || 0} hors catalogue`,
    });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        await uploadAudio(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setIsRecording(true);
      setElapsed(0);
      const started = Date.now();
      timerRef.current = setInterval(() => setElapsed(Date.now() - started), 200);
    } catch {
      toast.error("Micro refusé", {
        description:
          "Autorise l'accès au micro (Safari iOS : Réglages → Safari → Micro).",
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
  };

  const uploadAudio = async (blob) => {
    setIsProcessing(true);
    try {
      const fd = new FormData();
      const ext = blob.type.includes("webm") ? "webm" : "m4a";
      fd.append("file", blob, `dictation.${ext}`);
      const { data } = await api.post(`/quotes/${quoteId}/dictate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      handleResult(data);
    } catch (err) {
      toast.error("Échec du traitement", { description: formatApiError(err) });
    } finally {
      setIsProcessing(false);
    }
  };

  const structureText = async () => {
    const text = freeText.trim();
    if (!text) {
      toast.error("Texte vide");
      return;
    }
    setIsProcessing(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/structure-text`, { text });
      handleResult(data);
    } catch (err) {
      toast.error("Structuration échouée", { description: formatApiError(err) });
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleLine = (idx) => setChecked((c) => ({ ...c, [idx]: !c[idx] }));
  const editLine = (idx, patch) => {
    setResult((r) => ({
      ...r,
      lines: r.lines.map((l) => (l._idx === idx ? { ...l, ...patch } : l)),
    }));
  };
  const selectedLines = result?.lines?.filter((l) => checked[l._idx]) || [];

  const inject = () => {
    if (selectedLines.length === 0) {
      toast.error("Sélectionne au moins une ligne à injecter");
      return;
    }
    // Nettoie les lignes pour setQuote (retire _idx, garde tout le reste)
    const payload = selectedLines.map((l) => {
      const { _idx: _unused, ...rest } = l;
      return rest;
    });
    onInject({ lines: payload, raw_notes: result.raw_notes || "" });
    toast.success(`${payload.length} ligne(s) ajoutée(s) au devis`);
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-slate-950/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="dictate-modal"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-800 bg-slate-900/80 px-4 py-3 pt-safe">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#F0C572]" />
          <div>
            <h2 className="text-base font-semibold text-slate-100">Assistant vocal IA</h2>
            <p className="text-[10px] text-slate-500">
              Dictée ou texte libre — sélection puis injection dans le devis
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
          data-testid="dictate-modal-close"
        >
          <X className="mr-1 inline h-4 w-4" /> Fermer
        </button>
      </div>

      {/* Body scrollable */}
      <div className="flex-1 overflow-y-auto p-4">
        {!result ? (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mx-auto grid w-full max-w-md grid-cols-2 border border-slate-800 bg-slate-900/60">
              <TabsTrigger value="audio" data-testid="dictate-tab-audio">
                <Mic className="mr-2 h-4 w-4" /> Dicter
              </TabsTrigger>
              <TabsTrigger value="text" data-testid="dictate-tab-text">
                <Type className="mr-2 h-4 w-4" /> Coller texte
              </TabsTrigger>
            </TabsList>

            <TabsContent value="audio" className="mt-6">
              <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-4">
                {!isRecording ? (
                  <button
                    onClick={startRecording}
                    disabled={isProcessing}
                    className="flex h-40 w-40 items-center justify-center rounded-full bg-[#C4553B] text-white shadow-2xl transition hover:bg-[#A8402C] disabled:opacity-40"
                    data-testid="dictate-btn-start"
                    aria-label="Démarrer l'enregistrement"
                  >
                    <Mic className="h-14 w-14" />
                  </button>
                ) : (
                  <button
                    onClick={stopRecording}
                    className="flex h-40 w-40 items-center justify-center rounded-full bg-red-500 text-white shadow-2xl transition hover:bg-red-600"
                    data-testid="dictate-btn-stop"
                    aria-label="Arrêter l'enregistrement"
                  >
                    <Square className="h-12 w-12" />
                  </button>
                )}
                <div className="text-3xl font-mono tabular text-slate-100">
                  {formatElapsed(elapsed)}
                </div>
                {isRecording && (
                  <div className="flex items-end gap-1" aria-hidden="true">
                    {[3, 5, 7, 4, 6, 8, 5, 4].map((h, i) => (
                      <span
                        key={i}
                        className="w-1.5 rounded-full bg-red-400"
                        style={{
                          height: `${h * 5}px`,
                          animation: `pulse 0.9s ${i * 0.1}s ease-in-out infinite`,
                        }}
                      />
                    ))}
                  </div>
                )}
                {isProcessing && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Transcription et analyse en cours…
                  </div>
                )}
                <p className="mt-2 text-center text-xs text-slate-500">
                  Sécurité : ton audio et ta transcription <strong>ne sont jamais enregistrés</strong> côté serveur.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="text" className="mt-6">
              <div className="mx-auto max-w-2xl space-y-3">
                <Label className="text-slate-300">Colle ta description libre</Label>
                <Textarea
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  placeholder='Ex : "Toiture 120 m² démoussage, 15 tuiles à changer, tout à 6%"'
                  rows={8}
                  className="border-slate-700 bg-slate-950/40 text-slate-100"
                  data-testid="dictate-text-input"
                />
                <Button
                  onClick={structureText}
                  disabled={isProcessing || !freeText.trim()}
                  className="w-full bg-[#C4553B] text-white hover:bg-[#A8402C]"
                  data-testid="dictate-text-submit"
                >
                  {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Structurer
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="mx-auto max-w-4xl space-y-4">
            {/* Résumé */}
            <div className="flex flex-wrap gap-2">
              <Badge className="border-slate-700 bg-slate-800 text-slate-200" data-testid="dictate-summary-total">
                {result.lines.length} ligne(s)
              </Badge>
              <Badge className="border-emerald-500/40 bg-emerald-500/15 text-emerald-300">
                {result.catalog_matches_count} match(s) catalogue
              </Badge>
              {result.off_catalog_count > 0 && (
                <Badge className="border-[#D99A2B]/40 bg-[#D99A2B]/15 text-[#F0C572]">
                  {result.off_catalog_count} hors catalogue
                </Badge>
              )}
              {result.prefilter_applied && (
                <Badge className="border-slate-700 bg-slate-800 text-slate-400">
                  Pre-filter : {result.prefilter_candidates_count} candidats
                </Badge>
              )}
              <Badge className="border-slate-700 bg-slate-800 text-slate-400 tabular">
                {result.latency_ms_total} ms
              </Badge>
            </div>

            {result.transcription && (
              <div className="rounded border border-slate-800 bg-slate-900/40 p-3 text-xs text-slate-300">
                <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">
                  Transcription brute (non enregistrée)
                </div>
                <p className="italic" data-testid="dictate-transcription">{result.transcription}</p>
              </div>
            )}

            {result.raw_notes && (
              <div className="rounded border border-[#D99A2B]/30 bg-[#D99A2B]/10 p-3 text-xs">
                <div className="mb-1 text-[10px] uppercase tracking-wider text-[#F0C572]">
                  Notes de chantier (ajoutées aux notes du devis)
                </div>
                <p className="text-slate-200" data-testid="dictate-raw-notes">{result.raw_notes}</p>
              </div>
            )}

            {/* Preview lignes */}
            {result.lines.length === 0 ? (
              <div className="rounded border border-dashed border-slate-700 p-6 text-center text-sm text-slate-400">
                Aucune ligne exploitable détectée.
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-[24px_1fr_80px_60px_80px_60px] gap-2 px-2 text-[10px] uppercase tracking-wider text-slate-500">
                  <span></span>
                  <span>Description</span>
                  <span className="text-center">Qté</span>
                  <span className="text-center">Unité</span>
                  <span className="text-center">PU HT</span>
                  <span className="text-right">TVA</span>
                </div>
                {result.lines.map((l) => (
                  <LineRowPreview
                    key={l._idx}
                    line={l}
                    checked={!!checked[l._idx]}
                    onToggle={() => toggleLine(l._idx)}
                    onEdit={(patch) => editLine(l._idx, patch)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer d'action (visible quand résultats) */}
      {result && (
        <div className="border-t border-slate-800 bg-slate-900/80 px-4 py-3 pb-safe">
          <div className="mx-auto flex max-w-4xl items-center justify-between gap-3">
            <button
              onClick={() => setResult(null)}
              className="text-sm text-slate-400 underline-offset-2 hover:text-slate-200 hover:underline"
              data-testid="dictate-back-btn"
            >
              ← Reformuler
            </button>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400">
                {selectedLines.length} / {result.lines.length} sélectionnée(s)
              </span>
              <Button
                onClick={inject}
                disabled={selectedLines.length === 0}
                className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
                data-testid="dictate-inject-btn"
              >
                <PlusCircle className="mr-2 h-4 w-4" />
                Injecter dans le devis
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
